import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Text,
  Title,
  Flex,
  Icon,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  EmptyState,
  Spinner,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@kognitos/lattice';
import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { FilterBuilder } from '@/shared/utils';
import { isReturningToConnectWizard } from '@/modules/credentials';
import {
  getConnectionsListByIntegrations,
  getLatestVersionIntegrations,
  type IGroupedConnectionsByIntegration,
} from '@/modules/integrations/utils';
import { useExtractAutomationCodeConnections } from '@/generated/hooks/grimoire/v1/api';
import {
  useRetrieveAllWorkspaceBookConnections,
  useRetrieveLatestBooks,
} from '@/generated/hooks/bdk/v2/services/library';
import { useUpgradeAutomationConnection } from '@/modules/automation/hooks/use-upgrade-automation-connection';

import AutomationIntegrationItem from './AutomationIntegrationItem';
import { AUTOMATION_MENU_OAUTH_ORIGIN } from './AutomationConnectionMenu';

interface IAutomationIntegrationsPanelProps {
  automationId: string;
  isDraft: boolean;
  readonly?: boolean;
  /** `rail` is the full-width draft rail action, `railIcon` the 24px "+" that
   * sits in a draft rail block header. Both replace the compact header icon. */
  triggerVariant?: 'icon' | 'rail' | 'railIcon';
}

/**
 * Corner dot marking a trigger whose connections need attention. Every trigger
 * variant renders this same element, so the variants can't drift apart.
 * Decorative: the popover content carries the actual explanation.
 */
function IntegrationsActionRequiredIndicator() {
  return (
    <Flex
      aria-hidden
      data-testid="integrations-action-required-indicator"
      className="absolute -end-1 -top-1 size-2.5 rounded-full border-2 border-buffer bg-destructive"
    />
  );
}

/**
 * Panel displaying automation integrations and their connection details.
 * Wraps the panel content in a Popover with a trigger button.
 */
function AutomationIntegrationsPanel({
  automationId,
  isDraft,
  readonly = false,
  triggerVariant = 'icon',
}: IAutomationIntegrationsPanelProps) {
  const { t: tAutomation } = useTranslation('automation');
  const { t: tCommon } = useTranslation('common');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    isLoading: isLoadingAutomationConnections,
    isError: isErrorAutomationConnections,
    data: automationsConnectionsData,
    isRefetching: isRefetchingAutomationConnections,
  } = useExtractAutomationCodeConnections(
    currentOrganizationId,
    currentWorkspaceId,
    automationId,
    {
      filter: FilterBuilder.create()
        .equals('stage', isDraft ? 'DRAFT' : 'PUBLISHED')
        .build(),
    },
    {
      query: {
        enabled: Boolean(automationId),
      },
    },
  );

  const automationConnections = useMemo(
    () => automationsConnectionsData?.data?.connections ?? [],
    [automationsConnectionsData],
  );

  // Extract unique book_name and book_version pairs from automationConnections
  const getBookVersionPairs = () => {
    const pairs = new Map<string, { book: string; book_version: string }>();
    automationConnections.forEach(conn => {
      if (conn.book && conn.book_version) {
        const key = `${conn.book}:${conn.book_version}`;
        if (!pairs.has(key)) {
          pairs.set(key, { book: conn.book, book_version: conn.book_version });
        }
      }
    });
    return Array.from(pairs.values());
  };

  // Build filter: (book_name = "X" AND book_version = "Y") OR (book_name = "A" AND book_version = "B")
  const getWorkspaceConnectionsFilter = () => {
    const bookVersionPairs = getBookVersionPairs();
    if (bookVersionPairs.length === 0) {
      return undefined;
    }

    const orBuilders = bookVersionPairs.map(pair =>
      FilterBuilder.create()
        .equals('book_name', pair.book)
        .equals('book_version', pair.book_version),
    );

    return FilterBuilder.create()
      .or(...orBuilders)
      .build();
  };

  const workspaceConnectionsFilter = getWorkspaceConnectionsFilter();

  const {
    data: workspaceConnectionsData,
    isLoading: isLoadingWorkspaceConnections,
    isError: isErrorWorkspaceConnections,
    isRefetching: isRefetchingWorkspaceConnections,
  } = useRetrieveAllWorkspaceBookConnections(
    currentOrganizationId,
    currentWorkspaceId,
    workspaceConnectionsFilter
      ? {
          filter: workspaceConnectionsFilter,
        }
      : undefined,
    {
      query: {
        enabled: Boolean(workspaceConnectionsFilter),
      },
    },
  );

  const workspaceConnections = workspaceConnectionsData?.data.book_connections;

  // Collect unique book names so we can fetch their latest versions in one call.
  const uniqueBookNames = useMemo(() => {
    const names = new Set<string>();
    automationConnections.forEach(conn => {
      if (conn.book) names.add(conn.book);
    });
    return [...names];
  }, [automationConnections]);

  const latestBooksFilter = useMemo(() => {
    if (uniqueBookNames.length === 0) return undefined;
    const orBuilders = uniqueBookNames.map(name =>
      FilterBuilder.create().equals('name', name),
    );
    return uniqueBookNames.length === 1
      ? orBuilders[0].build()
      : FilterBuilder.create()
          .or(...orBuilders)
          .build();
  }, [uniqueBookNames]);

  const { data: latestBooksData } = useRetrieveLatestBooks(
    { page_size: 100, filter: latestBooksFilter },
    { query: { enabled: Boolean(latestBooksFilter) } },
  );

  // Map bookName → latestVersion string
  const latestVersionsMap = useMemo(() => {
    const books = latestBooksData?.data?.books ?? [];
    return new Map(
      getLatestVersionIntegrations(books)
        .filter(b => b.name && b.version)
        .map(b => [b.name!, b.version!]),
    );
  }, [latestBooksData]);

  // Single upgrade hook for the whole panel — covers all groups (books/versions).
  const { upgrade, isUpgrading, upgradingBook } =
    useUpgradeAutomationConnection({
      automationId,
      isDraft,
    });

  const handleUpgrade = useCallback(
    (
      bookName: string,
      currentBook?: Bdkv2BookDescriptor,
      latestBook?: Bdkv2BookDescriptor,
      automationGroupConnections?: IGroupedConnectionsByIntegration['connections'],
    ) => {
      const targetVersion = latestVersionsMap.get(bookName);
      if (!targetVersion) return;

      // Only re-auth connections this automation actually uses, not every
      // workspace connection for the book.
      const usedConnectionIds = new Set(
        (automationGroupConnections ?? []).flatMap(c =>
          [c.test_connection_id, c.production_connection_id].filter(
            (id): id is string => Boolean(id),
          ),
        ),
      );

      const bookConnections = (workspaceConnections ?? []).filter(
        c =>
          c.book_name === bookName &&
          (!usedConnectionIds.size ||
            (!!c.connection_id && usedConnectionIds.has(c.connection_id))),
      );

      upgrade({
        book: bookName,
        targetVersion,
        currentBook,
        latestBook,
        connections: bookConnections,
      });
    },
    [upgrade, latestVersionsMap, workspaceConnections],
  );

  const groupedConnections = getConnectionsListByIntegrations(
    automationConnections,
    workspaceConnections,
  );

  // Check if any connection has missing draft or production credentials
  const integrationsActionRequired = useMemo(() => {
    if (!groupedConnections || groupedConnections.length === 0) {
      return false;
    }

    return groupedConnections.some(group =>
      group.connections.some(
        conn =>
          !conn.test_credentials_available ||
          !conn.production_credentials_available,
      ),
    );
  }, [groupedConnections]);

  const isLoading =
    isLoadingAutomationConnections || isLoadingWorkspaceConnections;
  const isError = isErrorAutomationConnections || isErrorWorkspaceConnections;
  // Only flag the trigger once the connections are actually known.
  const showActionRequired =
    !isLoading && !isError && integrationsActionRequired;
  const isEmpty =
    !isLoading &&
    !isError &&
    (!groupedConnections || groupedConnections.length === 0);

  const isRefetching =
    isRefetchingAutomationConnections || isRefetchingWorkspaceConnections;

  const renderContent = () => {
    if (isError) {
      return (
        <Flex justify="center" align="center" width="full" height="full">
          <EmptyState
            icon="AlertCircle"
            description={tCommon('common.error.title')}
          />
        </Flex>
      );
    }

    if (isLoading || isRefetching) {
      return (
        <Flex justify="center" align="center" className="h-40" width="full">
          <Spinner />
        </Flex>
      );
    }

    if (isEmpty) {
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap={2}
          className="py-8"
          width="full"
        >
          <Icon type="Blocks" size="lg" className="text-muted-foreground" />
          <Text color="muted" level="small" className="text-center">
            {tAutomation('automation.integrations.empty')}
          </Text>
        </Flex>
      );
    }

    return (
      <>
        <Text level="small" color="muted">
          {tAutomation('automation.integrations.description')}
        </Text>
        {groupedConnections.map(group => (
          <AutomationIntegrationItem
            readonly={readonly}
            automationId={automationId}
            key={`${group.book}:${group.book_version}`}
            integrationWithConnections={group}
            isDraft={isDraft}
            latestVersion={latestVersionsMap.get(group.book)}
            isUpgrading={isUpgrading && upgradingBook === group.book}
            onUpgrade={(currentBook, latestBook) =>
              handleUpgrade(
                group.book,
                currentBook,
                latestBook,
                group.connections,
              )
            }
          />
        ))}
      </>
    );
  };

  // The connection menus (and their sign-in resume) live inside the popover,
  // so it opens when the browser comes back from a sign-in.
  const [opensForSignInReturn] = useState(() =>
    isReturningToConnectWizard(AUTOMATION_MENU_OAUTH_ORIGIN),
  );

  return (
    <Popover defaultOpen={opensForSignInReturn}>
      {triggerVariant === 'railIcon' ? (
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon-xs"
            className="relative"
            aria-label={tAutomation('automation.draftRail.addIntegration')}
          >
            <Icon type="Plus" size="xs" />
            {showActionRequired && <IntegrationsActionRequiredIndicator />}
          </Button>
        </PopoverTrigger>
      ) : triggerVariant === 'rail' ? (
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="relative w-full border-dashed"
          >
            <Icon type="Plus" size="xs" />
            {tAutomation('automation.draftRail.addIntegration')}
            {showActionRequired && <IntegrationsActionRequiredIndicator />}
          </Button>
        </PopoverTrigger>
      ) : (
        <Tooltip delayDuration={200} variant="primary">
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                className="relative"
                aria-label={tAutomation(
                  'automation.integrations.integrationsUsedAriaLabel',
                )}
              >
                <Icon type="Blocks" size="sm" />
                {showActionRequired && <IntegrationsActionRequiredIndicator />}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            {tAutomation('automation.header.tooltip.integrations')}
          </TooltipContent>
        </Tooltip>
      )}
      <PopoverContent align="end" className="w-md bg-background">
        <Flex direction="column" gap={4} width="full">
          <Title level="h4" className="text-base shrink-0">
            {tAutomation('automation.integrations.title')}
          </Title>
          <Flex
            direction="column"
            gap={4}
            width="full"
            className="max-h-[60vh] overflow-y-auto"
          >
            {renderContent()}
          </Flex>
        </Flex>
      </PopoverContent>
    </Popover>
  );
}

export default AutomationIntegrationsPanel;
